import React, { useRef, useState, useEffect } from 'react';
import { Col, Label, ModalBody, ModalHeader, Row } from 'reactstrap';
import './UpdateVideoClipPopup.scss';
import { Field } from '@availity/form';
import { Formik, useFormikContext } from 'formik';
import * as yup from 'yup';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import Rodal from '../../../../components/Rodal/Rodal';
import { VideoClip } from '../SaveVideoClipPopup/SaveVideoClipPopup';
import createNotification from '../../../../utils/createNotification';
import { useStateSelector } from '../../../../store/selectors';

interface UpdateVideoClipPopupProps {
    clip: VideoClip | null;
    groupId?: number;
    engagementId?: number;
    meetingId: number;
    isVisible: boolean;
    onClose: () => void;
    onClipUpdated: () => void;
    type?: 'group' | 'engagement-meetings' | 'marketplace-meetings';
}

const FormContent = (props: {
    initialTitle: string;
    initialDescription: string;
    onSaveButtonDisabled: (disabled: boolean) => void;
}) => {
    const { values } = useFormikContext<{ title: string; description: string }>();

    useEffect(() => {
        const hasChanges =
            values.title.trim() !== props.initialTitle.trim() ||
            values.description.trim() !== props.initialDescription.trim();
        const isValid =
            values.title.trim().length > 0 &&
            values.description.trim().length > 0;
        props.onSaveButtonDisabled(!hasChanges || !isValid);
    }, [values.title, values.description, props.initialTitle, props.initialDescription]);

    return (
        <>
            <Row>
                <Col md={3}>
                    <Label for="title">Title:</Label>
                </Col>
                <Col md={9} className="flex-container">
                    <Field
                        type="text"
                        name="title"
                        placeholder="Enter clip title"></Field>
                </Col>
            </Row>
            <Row>
                <Col md={3}>
                    <Label for="description">Description:</Label>
                </Col>
                <Col md={9} className="flex-container">
                    <Field
                        className="textarea"
                        type="textarea"
                        name="description"
                        placeholder="Enter clip description"></Field>
                </Col>
            </Row>
        </>
    );
};

export const UpdateVideoClipPopup = (props: UpdateVideoClipPopupProps) => {
    const [isSaving, setIsSaving] = useState(false);
    const formRef = useRef<any>(null);
    const axios = useStateSelector((state) => state.core.axios);
    const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
    const [initialTitle, setInitialTitle] = useState('');
    const [initialDescription, setInitialDescription] = useState('');

    useEffect(() => {
        if (props.clip) {
            setInitialTitle(props.clip.title || '');
            setInitialDescription(props.clip.description || '');
            setSaveButtonDisabled(true);
        }
    }, [props.clip]);

    useEffect(() => {
        if (!props.isVisible) {
            setSaveButtonDisabled(true);
        }
    }, [props.isVisible]);

    const updateClip = async (title: string, description: string) => {
        if (!props.clip) return;

        let endpoint: string;

        if (props.type === 'engagement-meetings') {
            if (!props.engagementId) {
                createNotification('Engagement ID is required', 'error');
                setIsSaving(false);
                return;
            }
            endpoint = `/api/Marketplace/Engagements/${props.engagementId}/EngagementMeetings/${props.meetingId}/clips/${props.clip.id}`;
        } else if (props.type === 'marketplace-meetings') {
            endpoint = `/api/Marketplace/MarketplaceMeetings/${props.meetingId}/clips/${props.clip.id}`;
        } else {
            if (!props.groupId) {
                createNotification('Group ID is required', 'error');
                setIsSaving(false);
                return;
            }
            endpoint = `/api/groups/${props.groupId}/clips/${props.clip.id}`;
        }

        const body = {
            ConferenceId: props.clip.conferenceId,
            Title: title,
            Description: description,
        };

        try {
            const response = await axios.put(endpoint, body);
            if (response.status === 200) {
                createNotification(
                    'Video clip has been successfully updated',
                    'success'
                );
                props.onClose();
                props.onClipUpdated();
            } else {
                createNotification(
                    'Error occurred while updating video clip',
                    'error'
                );
            }
        } catch (error) {
            createNotification(
                'Error occurred while updating video clip',
                'error'
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleSubmit = (values: { title: string; description: string }) => {
        setIsSaving(true);
        updateClip(values.title, values.description);
    };

    return (
        <div className="update-video-clip-popup">
            <Rodal
                visible={props.isVisible && !!props.clip}
                closeMaskOnClick={false}
                closeOnEsc={true}
                width={450}
                onClose={props.onClose}
                animation={'fade'}
                showMask={true}>
                <ModalHeader>Update Video Clip</ModalHeader>
                <ModalBody>
                    <Formik
                        innerRef={formRef}
                        validationSchema={yup.object().shape({
                            title: yup.string().max(50).required('Title is required'),
                            description: yup
                                .string()
                                .max(200)
                                .required('Description is required'),
                        })}
                        onSubmit={handleSubmit}
                        initialValues={{
                            title: initialTitle,
                            description: initialDescription,
                        }}
                        enableReinitialize={true}>
                        <FormContent
                            initialTitle={initialTitle}
                            initialDescription={initialDescription}
                            onSaveButtonDisabled={setSaveButtonDisabled}
                        />
                    </Formik>
                    <div className="mt-auto d-flex justify-content-end buttons-container">
                        <button
                            className="btn btn-outline-secondary mr-2"
                            onClick={props.onClose}>
                            Cancel
                        </button>
                        <ButtonLoader
                            buttonText="Save"
                            loaderButtonText=""
                            disabled={saveButtonDisabled}
                            isLoading={isSaving}
                            onClick={() => formRef.current?.handleSubmit()}
                            className="btn btn-primary"></ButtonLoader>
                    </div>
                </ModalBody>
            </Rodal>
        </div>
    );
};

